import { Button } from "primereact/button";
import { DataView } from "primereact/dataview";
import { InputText } from "primereact/inputtext";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { logService } from "src/core/services/logService";
import { viewService } from "src/core/services/viewService";
import { useAppointmentStore } from "src/features/appointment/services/useAppointmentStore";
import { useCalendarStore } from "src/features/calendar/services/useCalendarStore";
import { DoctorProfileModel } from "src/features/clinics/models/doctorProfileModel";
import { useClinicsStore } from "src/features/clinics/services/useClinicsStore";
import { useFavouriteStore } from "src/features/favourite/services/useFavouriteStore";
import { useSettingsStore } from "src/features/settings/services/useSettingsStore";
import type { AppointmentStepProps } from "../../AppointmentPage";
import "./AppointmentStep3.css";

export function AppointmentStep3({
  activeStepIndex,
  setActiveStepIndex,
  previousStepIndex,
}: AppointmentStepProps) {
  const searchNameRef = useRef<HTMLInputElement>(null);

  const { t } = useTranslation();

  const clinicsStore = useClinicsStore();
  const favouriteStore = useFavouriteStore();
  const calendarStore = useCalendarStore();
  const appointmentStore = useAppointmentStore();
  const settingsStore = useSettingsStore();

  const [doctorProfiles, setDoctorProfiles] = useState<DoctorProfileModel[]>(
    []
  );

  const [searchName, setSearchName] = useState("");
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);

  const searchNameFocus = () => {
    setTimeout(() => {
      searchNameRef.current?.focus();
    }, 100);
  };

  const onSearchNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setSearchName(value);
    }, 500);
  };

  useEffect(() => {
    appointmentStore.setSelectedDoctorProfile(undefined);
    appointmentStore.setSelectedDateTime(undefined);

    calendarStore.clear();

    if (!viewService.isMobileSize) searchNameFocus();
  }, []);

  useEffect(() => {
    const doctorProfiles = clinicsStore.clinicDoctors
      .filter((clinicDoctor) => {
        return (
          searchName?.length === 0 ||
          clinicDoctor.doctorName
            ?.toLowerCase()
            .includes(searchName?.toLowerCase() ?? "")
        );
      })
      .filter((clinicDoctor) => {
        return clinicDoctor.clinicId === appointmentStore.selectedClinic?.id;
      })
      .flatMap((clinicDoctor) =>
        clinicDoctor.profiles.map((profile) => {
          const favourite = favouriteStore.favourites.some(
            (fav) =>
              fav.clinicId === clinicDoctor.clinicId &&
              fav.doctorId === clinicDoctor.doctorId
          );

          return new DoctorProfileModel({
            doctorId: clinicDoctor.doctorId,
            doctorName: clinicDoctor.doctorName,
            doctorImagePath: clinicDoctor.doctorImagePath,
            doctorPhoneRequired: clinicDoctor.doctorPhoneRequired,
            profileId: profile.id,
            profileName: profile.name === "Általános" ? "" : profile.name ?? "",
            profileActivity: profile.activity,
            profileDescription: profile.description,
            favourite: favourite,
            order:
              (favourite ? "0" : "1") + clinicDoctor.doctorName + profile.name,
          });
        })
      )
      .sort((a, b) => a.order.localeCompare(b.order, "hu"));

    if (logService.globalLog) logService.log("doctorProfiles", doctorProfiles);

    const doctorProfilesFiltered = doctorProfiles.filter(
      (doctorProfile) =>
        doctorProfile.doctorId ===
        appointmentStore.selectedDoctorProfile?.doctorId
    );

    if (doctorProfiles.length === 1 && activeStepIndex > previousStepIndex) {
      onDoctorProfileSelect(doctorProfiles[0]);
      return;
    } else if (
      doctorProfilesFiltered.length === 1 &&
      activeStepIndex > previousStepIndex
    ) {
      onDoctorProfileSelect(doctorProfilesFiltered[0]);
      return;
    }

    setDoctorProfiles(doctorProfiles);
  }, [clinicsStore.clinicDoctors, favouriteStore.favourites, searchName]);

  const onDoctorProfileSelect = (doctorProfile: DoctorProfileModel) => {
    appointmentStore.setSelectedDoctorProfile(doctorProfile);

    setActiveStepIndex(++activeStepIndex);
  };

  const hideSearch =
    settingsStore.clientConfiguration.subMode?.includes("hidesearch");

  return (
    <div className="appointmentStep3">
      <div className="col-12">
        <DataView
          value={doctorProfiles}
          header={
            <div className="flex flex-column sm:flex-row gap-3">
              <div className="flex flex-1">
                <Button
                  icon="pi pi-chevron-left"
                  label={t("Home.Appointments.Step2Title")}
                  onClick={() => {
                    setActiveStepIndex(--activeStepIndex);
                  }}
                ></Button>
              </div>
              <div className="flex flex-1 justify-content-center align-items-center gap-2">
                <div className="flex flex-column justify-content-center gap-1">
                  {hideSearch ? (
                    <h2 className="m-0 p-0">
                      {viewService.isMobileSize
                        ? ""
                        : t("Home.Appointments.Step3Title")}
                    </h2>
                  ) : (
                    <div className="flex align-items-center gap-1">
                      <div className="font-bold">{t("Base.Search")}</div>

                      <div className="p-inputgroup align-items-center searchInput">
                        <InputText
                          ref={searchNameRef}
                          id="searchName"
                          className="searchName w100"
                          defaultValue={searchName}
                          onChange={onSearchNameChange}
                          type="text"
                        />
                        <Button
                          icon="pi pi-times"
                          severity="warning"
                          onClick={() => {
                            if (searchNameRef.current)
                              searchNameRef.current.value = "";
                            setSearchName("");
                            searchNameFocus();
                          }}
                        ></Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-1"></div>
            </div>
          }
          listTemplate={(items?: DoctorProfileModel[]) => {
            if (!items || items.length === 0) return null;

            let list = items.map((item, index) => {
              return (
                <div
                  key={item.profileId}
                  className={
                    "col-12 sm:col-6 lg:col-4 xl:col-3 p-2 flex align-items-stretch " +
                    (index > 0 ? "border-top-1 surface-border" : "")
                  }
                >
                  <Button
                    text
                    raised
                    className="flex flex-column align-items-center gap-1 text-xl w100"
                    onClick={() => {
                      onDoctorProfileSelect(item);
                    }}
                  >
                    {!item.doctorImagePath && (
                      <i
                        className="pi pi-plus-circle"
                        style={{ fontSize: "2rem" }}
                      ></i>
                    )}
                    {item.doctorImagePath && (
                      <img
                        src={item.doctorImagePath}
                        alt={item.doctorName}
                        style={{ maxWidth: "150px" }}
                      />
                    )}
                    <div className="flex flex-row align-items-center gap-1">
                      {item.favourite && <i className="pi pi-star-fill"></i>}
                      <span className="font-bold">{item.doctorName}</span>
                    </div>
                    <div className="font-italic">{item.profileName}</div>
                  </Button>
                </div>
              );
            });

            return (
              <div className="grid grid-nogutter justify-content-center">
                {list}
              </div>
            );
          }}
        />
      </div>
    </div>
  );
}
